/**
 * Auth Context
 * 인증 상태 관리
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { authAPI } from '../services/api';
import { getFirebaseErrorMessage } from '../utils/firebaseError';

interface AuthContextType {
  user: User | null;
  userInfo: UserInfo | null;
  loading: boolean;
  signup: (data: SignupData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkServiceAccess: (serviceType: string) => boolean;
  refreshUserInfo: () => Promise<void>;
}

interface UserInfo {
  uid: string;
  email: string;
  displayName: string;
  memberGrade: string;
  referralCode: string;
  creditBalance: number;
  currentSubscription?: {
    type: string;
    expiresAt: Date;
  };
  oneTimePurchases?: string[];
}

interface SignupData {
  email: string;
  password: string;
  displayName: string;
  phoneNumber?: string;
  referralCode?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Firebase Auth 상태 변경 감지
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // ID Token 가져오기
        const idToken = await firebaseUser.getIdToken();

        // 사용자 정보 조회
        try {
          const response: any = await authAPI.verifyToken(idToken);
          if (response.success) {
            setUserInfo(response.data);
          }
        } catch (error) {
          console.error('Failed to fetch user info:', error);
          setUserInfo(null);
        }
      } else {
        setUserInfo(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signup = async (data: SignupData) => {
    try {
      // 1. Firebase Auth로 사용자 생성
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);

      // 2. Functions 호출하여 Custom Claims 설정 (재시도 로직 포함)
      const maxRetries = 3;
      const retryDelay = 1000; // 1초
      let lastError: any = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`[Signup] authAPI.signup attempt ${attempt}/${maxRetries}`);
          await authAPI.signup(data);
          console.log(`[Signup] authAPI.signup succeeded on attempt ${attempt}`);
          lastError = null;
          break; // 성공 시 루프 종료
        } catch (error: any) {
          lastError = error;
          console.error(`[Signup] authAPI.signup failed on attempt ${attempt}:`, error);

          // 마지막 시도가 아니면 재시도
          if (attempt < maxRetries) {
            console.log(`[Signup] Retrying in ${retryDelay}ms...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }
      }

      // 모든 재시도 실패 시
      if (lastError) {
        console.error('[Signup] All authAPI.signup attempts failed, but user is created in Firebase Auth');
        console.error('[Signup] User can still login - orphan recovery will fix Firestore document');
        // 사용자에게 로그인을 시도하도록 안내
        throw new Error('회원가입이 완료되었습니다. 로그인을 진행해주세요.');
      }

      // 3. 토큰 강제 새로고침 (Custom Claims 반영)
      await userCredential.user.getIdToken(true);

    } catch (error: any) {
      console.error('Signup error:', error);
      const friendlyMessage = getFirebaseErrorMessage(error);
      throw new Error(friendlyMessage);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      // 1. Firebase Auth로 먼저 로그인 (비밀번호 검증)
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // 2. authAPI.login()으로 Custom Claims 동기화 및 고아 계정 복구
      // 비밀번호 검증 후에만 실행되므로 잘못된 비밀번호로 Firestore 문서 생성 방지
      await authAPI.login({ email, password });

      // 3. 토큰 강제 새로고침 (Custom Claims 반영)
      await userCredential.user.getIdToken(true);

    } catch (error: any) {
      console.error('Login error:', error);
      const friendlyMessage = getFirebaseErrorMessage(error);
      throw new Error(friendlyMessage);
    }
  };

  const refreshUserInfo = async () => {
    if (user) {
      // Custom claims가 업데이트된 경우에만 강제 refresh 필요
      // 일반적인 경우 캐시된 토큰 사용으로 성능 개선
      const idToken = await user.getIdToken();
      try {
        const response: any = await authAPI.verifyToken(idToken);
        if (response.success) {
          console.log('[AuthContext] User info refreshed:', response.data);
          setUserInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to refresh user info:', error);
      }
    }
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserInfo(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const checkServiceAccess = (serviceType: string): boolean => {
    console.log('[AuthContext] Checking service access:', {
      serviceType,
      userInfo,
      memberGrade: userInfo?.memberGrade,
      hasSubscription: !!userInfo?.currentSubscription,
      oneTimePurchases: userInfo?.oneTimePurchases
    });

    if (!userInfo) {
      console.log('[AuthContext] No userInfo, access denied');
      return false;
    }

    // regular, special, admin 등급은 모든 서비스 무제한 접근
    if (userInfo.memberGrade === 'regular' || userInfo.memberGrade === 'special' || userInfo.memberGrade === 'admin') {
      console.log('[AuthContext] Access granted via member grade (free)');
      return true;
    }

    // 구독 확인
    if (userInfo.currentSubscription) {
      try {
        const expiresAt = userInfo.currentSubscription.expiresAt instanceof Date
          ? userInfo.currentSubscription.expiresAt
          : new Date(userInfo.currentSubscription.expiresAt);

        const now = new Date();
        console.log('[AuthContext] Subscription check:', {
          expiresAt: expiresAt.toISOString(),
          now: now.toISOString(),
          isValid: expiresAt > now
        });

        if (expiresAt > now) {
          console.log('[AuthContext] Access granted via subscription');
          return true;
        }
      } catch (error) {
        console.error('[AuthContext] Failed to parse subscription date:', error);
      }
    }

    // 2. 일회성 구매 확인
    if (userInfo.oneTimePurchases?.includes(serviceType)) {
      console.log('[AuthContext] Access granted via one-time purchase');
      return true;
    }

    console.log('[AuthContext] Access denied');
    return false;
  };

  const value: AuthContextType = {
    user,
    userInfo,
    loading,
    signup,
    login,
    logout,
    checkServiceAccess,
    refreshUserInfo
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};